import { Editor } from "@tinymce/tinymce-react";
import config from "config/config";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { spaceActions } from "redux/Ldb/actions/spaceActions";

type AddNotesProps = {
    setAddNote: (value: boolean) => void;
    addNote: boolean;
};

const AddNotes = ({ setAddNote, addNote }: AddNotesProps) => {
    const dispatch = useAppDispatch();
    const inputRef = useRef(null);
    const { spaceId } = useParams();
    const [note, setNote] = useState<any>({
        name: "",
        body: "",
    });
    const [noteId, setNoteId] = useState<string>("");
    const [updatedName, setUpdatedName] = useState<string>("");
    const [updatedBody, setUpdatedBody] = useState<string>("");
    const [failedToSave, setFailedToSave] = useState<boolean>(false);

    const {
        createSpaceNoteSuccess,
        creatingSpaceNote,
        updateSpaceNoteSuccess,
        updatingSpaceNote,
        updateSpaceNoteFailure,
    } = useAppSelector((state) => state.space);

    useEffect(() => {
        (inputRef?.current as any)?.focus();
    }, []);

    useEffect(() => {
        if (Boolean(updateSpaceNoteSuccess)) {
            setFailedToSave(false);
            dispatch(spaceActions.resetUpdateSpaceNote());
        }
    }, [dispatch, updateSpaceNoteSuccess]);

    useEffect(() => {
        if (Boolean(createSpaceNoteSuccess)) {
            setFailedToSave(false);
            setNoteId(createSpaceNoteSuccess?.data?.note?._id);
            dispatch(spaceActions.resetCreateSpaceNote());
        }
    }, [dispatch, createSpaceNoteSuccess]);

    const handleUpdateNoteName = debounce((e) => {
        setUpdatedName(e?.target?.value);
        setUpdatedBody(note?.body);
    }, 3000);

    useEffect(() => {
        if (!spaceId || !noteId) {
            return;
        }

        if (noteId) {
            dispatch(
                spaceActions.updateSpaceNote(spaceId, noteId, {
                    name: updatedName || "Untitled note",
                    body: updatedBody || "",
                })
            );
        }
    }, [dispatch, updatedName, updatedBody, noteId, spaceId]);

    useEffect(() => {
        if (!spaceId || noteId || (!updatedName && !updatedBody) || creatingSpaceNote) {
            return;
        }

        dispatch(
            spaceActions.createSpaceNote(spaceId, {
                name: updatedName || (updatedBody ? "Untitled note" : ""),
                body: updatedBody || "",
            })
        );
    }, [dispatch, updatedName, updatedBody, spaceId]);

    useEffect(() => {
        if (Boolean(updateSpaceNoteFailure)) {
            setFailedToSave(true);
            dispatch(spaceActions.resetUpdateSpaceNote());
        }
    }, [dispatch, updateSpaceNoteFailure]);

    const debouncedChangeHandler = useCallback(
        debounce((newValue: string, name: string) => {
            setUpdatedName(name);
            setUpdatedBody(newValue);
        }, 3000),
        []
    );

    return (
        <div className="">
            <div className="px-[24px] sticky top-0 bg-white z-10 flex items-center justify-between border-b border-b-slate-100 mb-4 pb-2.5">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => {
                        dispatch(spaceActions.getSpaceNotes(spaceId, 1, 20));
                        setAddNote(false);
                    }}
                >
                    <i className="ri-arrow-left-line text-sm text-slate-950 cursor-pointer"></i>
                    <p className="text-sm font-rocGroteskMedium text-slate-950 cursor-pointer">
                        Back to notes
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <i className="ri-cloud-line text-sm text-slate-500"></i>
                    <p className="text-[13px] font-rocGroteskMedium text-slate-500">
                        {failedToSave || !noteId
                            ? "Not Saved"
                            : creatingSpaceNote || updatingSpaceNote
                            ? "Saving..."
                            : "Saved"}
                    </p>
                </div>
            </div>

            {/* add note */}
            <div className="flex-1 flex items-center justify-center pb-6 mt-10">
                <div className={`flex  justify-center items-center`}>
                    <div className="xl:w-[650px] w-full text-start">
                        <div className="space-y-4">
                            <div className="pb-4 border-b border-b-slate-100">
                                <div className="flex items-center mb-2">
                                    <p className="text-xs font-rocGroteskMedium text-gm-50 ">
                                        Note name
                                    </p>
                                    <p className="text-sm text-r-50">*</p>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Untitled note"
                                    className="!text-2xl font-rocGroteskMedium  placeholder:text-slate-400 !border-none outline-none 
                                w-full caret-slate-700 !leading-6"
                                    ref={inputRef}
                                    name="name"
                                    value={note.name}
                                    onChange={(e) => setNote({ ...note, name: e.target.value })}
                                    onInput={handleUpdateNoteName}
                                />
                            </div>
                            <div className="pb-4">
                                <p className="text-xs font-rocGroteskMedium text-gm-50 mb-2">
                                    Description / Body
                                </p>
                                {/* <textarea
                                    placeholder="Enter your detailed note content here"
                                    className="!text-sm font-rocGroteskMedium  placeholder:text-slate-400 !border-none outline-none 
                                w-full !leading-6 resize-none h-[200px]"
                                    name="description"
                                    value={note.body}
                                    onChange={(e) => setNote({ ...note, body: e.target.value })}
                                    required
                                    onInput={handleUpdateNoteBody}
                                /> */}
                                <div className="my-editor-content">
                                    <Editor
                                        apiKey={config.TINYMICE_API}
                                        value={note?.body}
                                        onEditorChange={(newValue, editor) => {
                                            setNote({ ...note, body: newValue });
                                            debouncedChangeHandler(newValue, note?.name);
                                        }}
                                        init={{
                                            height: 200,
                                            menubar: false,
                                            plugins: [
                                                "advlist",
                                                "autolink",
                                                "lists",
                                                "link",
                                                "anchor",
                                                "searchreplace",
                                                "visualblocks",
                                                "fullscreen",
                                                "insertdatetime",
                                                "media",
                                            ],

                                            placeholder: "Enter your detailed note content here",
                                            // selector: "textarea",
                                            resize: false,
                                            branding: false,
                                            toolbar: false,
                                            body_class: "my-editor-content",
                                            content_style:
                                                "body { font-family:rocGrotesk,Helvetica,Arial,sans-serif; font-size:14px;  margin:0px !important;}",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddNotes;
